import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SiClassificazione } from "@/components/ai/SiClassificazione";
import { fmtIT } from "@/lib/dateIT";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FileScan, ArrowLeft, Upload, CheckCircle2, AlertTriangle, Loader2, FileText, ShieldCheck, ShieldX, Link2, Settings, Building2 } from "lucide-react";

const STATO = {
  elaborazione: { label: "In elaborazione", cls: "bg-amber-100 text-amber-700" },
  pronto: { label: "Pronto", cls: "bg-emerald-100 text-emerald-700" },
  confermato: { label: "Confermato", cls: "bg-blue-100 text-blue-700" },
  errore: { label: "Errore", cls: "bg-red-100 text-red-700" },
};

function Field({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-xs text-slate-500">{label}</label>
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="h-9" />
    </div>
  );
}

export default function SmartImport() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [sel, setSel] = useState(null);
  const [contraente, setContraente] = useState({});
  const [uploading, setUploading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const fileRef = useRef(null);
  const pollRef = useRef(null);
  const [cfg, setCfg] = useState(null);
  const [showCfg, setShowCfg] = useState(false);
  const [cfgForm, setCfgForm] = useState({});

  const loadList = useCallback(() => { api.get("/ai/smartimport").then((r) => setList(r.data)).catch(() => {}); }, []);
  const loadCfg = useCallback(() => { api.get("/ai/config").then((r) => { setCfg(r.data); setCfgForm(r.data); }).catch(() => {}); }, []);
  useEffect(() => { loadList(); loadCfg(); return () => clearInterval(pollRef.current); }, [loadList, loadCfg]);
  const [spSi] = useSearchParams();

  const saveCfg = async () => {
    try {
      const body = {
        extraction_model: cfgForm.extraction_model,
        visura_enabled: !!cfgForm.visura_enabled,
        visura_endpoint: cfgForm.visura_endpoint || "",
        visura_method: cfgForm.visura_method || "GET",
        visura_piva_param: cfgForm.visura_piva_param || "partita_iva",
        visura_auth_header: cfgForm.visura_auth_header || "Authorization",
        visura_auth_prefix: cfgForm.visura_auth_prefix ?? "Bearer ",
        visura_fonte: cfgForm.visura_fonte || "Provider visura",
      };
      if (cfgForm.visura_api_key) body.visura_api_key = cfgForm.visura_api_key;
      await api.put("/ai/config", body);
      toast.success("Impostazioni salvate");
      setShowCfg(false); loadCfg();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openDetail = useCallback(async (id) => {
    try {
      const { data } = await api.get(`/ai/smartimport/${id}`);
      setSel(data);
      setContraente((data.estratto?.contraente) || {});
      if (data.stato === "elaborazione") {
        clearInterval(pollRef.current);
        pollRef.current = setInterval(async () => {
          const { data: d2 } = await api.get(`/ai/smartimport/${id}`);
          if (d2.stato !== "elaborazione") { clearInterval(pollRef.current); setSel(d2); setContraente(d2.estratto?.contraente || {}); loadList(); }
        }, 3000);
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, [loadList]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/ai/smartimport", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento caricato, lettura in corso…");
      loadList();
      openDetail(data.id);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const conferma = async (useMatch) => {
    if (!sel) return;
    setConfirming(true);
    try {
      const body = { contraente, usa_match_id: useMatch ? sel.match_anagrafica?.id : null };
      const { data } = await api.post(`/ai/smartimport/${sel.id}/conferma`, body);
      toast.success("Anagrafica pronta. Apro la pratica precompilata…");
      navigate(data.deep_link);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setConfirming(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { const id = spSi.get("id"); if (id) openDetail(id); }, [spSi]);
  const est = sel?.estratto || {};
  const vies = sel?.vies;
  const tgt = sel?.routing?.target;
  const tgtLabel = tgt === "quotatore_mrv" ? "pratica Multirischi" : tgt === "mup" ? "MUP precompilato" : "pratica RC";

  return (
    <div className="min-h-screen bg-slate-50" data-testid="smart-import-page">
      <header className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} data-testid="si-back-btn"><ArrowLeft className="h-4 w-4" /></Button>
        <div className="flex items-center gap-2 text-cyan-700"><FileScan className="h-5 w-5" /><span className="font-semibold text-slate-800">Assietica Smart Import</span></div>
        <div className="ml-auto flex items-center gap-2">
          {cfg?.is_admin && (
            <Button variant="outline" size="sm" onClick={() => setShowCfg(true)} data-testid="si-settings-btn"><Settings className="h-4 w-4 mr-1" /> Impostazioni</Button>
          )}
          <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" onChange={onUpload} data-testid="si-file-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="si-upload-btn" className="bg-cyan-700 hover:bg-cyan-800">
            {uploading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />} Carica polizza
          </Button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6 p-4">
        <aside className="lg:col-span-1 space-y-2" data-testid="si-list">
          <p className="text-xs uppercase tracking-wide text-slate-400 px-1">Documenti</p>
          {list.length === 0 && <p className="text-sm text-slate-400 px-1 py-6">Nessun documento. Carica una polizza di altra compagnia per iniziare.</p>}
          {list.map((d) => (
            <button key={d.id} onClick={() => openDetail(d.id)} data-testid={`si-item-${d.id}`}
              className={`w-full text-left rounded-xl border p-3 bg-white hover:border-cyan-300 transition-colors ${sel?.id === d.id ? "border-cyan-400 ring-1 ring-cyan-200" : "border-slate-200"}`}>
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-slate-400 shrink-0" />
                <span className="text-sm font-medium text-slate-700 truncate">{d.original_filename}</span>
              </div>
              <div className="mt-1.5 flex items-center justify-between">
                <Badge className={STATO[d.stato]?.cls}>{STATO[d.stato]?.label || d.stato}</Badge>
                <span className="text-xs text-slate-400">{(d.created_at || "").slice(0, 10)}</span>
              </div>
            </button>
          ))}
        </aside>

        <main className="lg:col-span-2" data-testid="si-detail">
          {!sel && <div className="text-center text-slate-400 mt-24"><FileScan className="h-12 w-12 mx-auto mb-3 text-slate-200" /><p>Seleziona o carica un documento.</p></div>}

          {sel?.stato === "elaborazione" && <div className="text-center text-slate-500 mt-24"><Loader2 className="h-8 w-8 mx-auto mb-3 animate-spin text-cyan-600" /><p>Lettura ed estrazione dei dati in corso…</p></div>}

          {sel?.stato === "errore" && <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-700 flex items-center gap-2"><AlertTriangle className="h-5 w-5" /> {sel.errore || "Errore di elaborazione."}</div>}

          {sel && (sel.stato === "pronto" || sel.stato === "confermato") && (
            <div className="space-y-5">
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <Badge className="bg-slate-100 text-slate-700">Tipo: {est.tipo_documento || "—"}</Badge>
                  <Badge className="bg-cyan-100 text-cyan-800">Ramo: {est.ramo || "—"}</Badge>
                  <Badge className="bg-slate-100 text-slate-700">Compagnia: {est.compagnia_emittente || "—"}</Badge>
                  <Badge className="bg-slate-100 text-slate-700">Confidenza: {est.confidenza || "—"}</Badge>
                </div>
              </div>

              {sel.match_anagrafica ? (
                <div className="rounded-xl border border-blue-200 bg-blue-50 p-4" data-testid="si-match">
                  <div className="flex items-center gap-2 text-blue-800 font-medium"><Link2 className="h-4 w-4" /> Cliente già presente in archivio</div>
                  <p className="text-sm text-blue-700 mt-1">{sel.match_anagrafica.ragione_sociale} — match su {sel.match_anagrafica.match_su}</p>
                </div>
              ) : (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="si-nomatch">Nessun cliente corrispondente in archivio: verrà proposta la creazione di una nuova anagrafica.</div>
              )}

              {vies && (
                <div className="rounded-xl border border-slate-200 bg-white p-4 flex items-center gap-2 text-sm" data-testid="si-vies">
                  {vies.valid ? <ShieldCheck className="h-5 w-5 text-emerald-600" /> : <ShieldX className="h-5 w-5 text-slate-400" />}
                  <span className="font-medium">VIES P.IVA:</span>
                  <span>{vies.valid ? "valida" : vies.valid === false ? "non risultante" : "non verificabile"}</span>
                  <span className="text-slate-400 ml-auto text-xs">Fonte: {vies.fonte}</span>
                </div>
              )}

              {sel.visura?.disponibile && (
                <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-4 text-sm" data-testid="si-visura">
                  <div className="flex items-center gap-2 text-indigo-800 font-medium"><Building2 className="h-4 w-4" /> Dati impresa (visura)</div>
                  <p className="text-indigo-700 mt-1">{sel.visura.normalizzato?.ragione_sociale || "—"} · {sel.visura.normalizzato?.comune || ""} {sel.visura.normalizzato?.provincia ? `(${sel.visura.normalizzato.provincia})` : ""}{sel.visura.normalizzato?.natura_giuridica ? ` · ${sel.visura.normalizzato.natura_giuridica}` : ""}</p>
                  <span className="text-indigo-400 text-xs">Fonte: {sel.visura.fonte}</span>
                </div>
              )}

              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <p className="font-medium text-slate-800 mb-3">Dati contraente estratti <span className="text-xs text-slate-400">(verifica e correggi prima di confermare)</span></p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Ragione sociale" value={contraente.ragione_sociale} onChange={(v) => setContraente((c) => ({ ...c, ragione_sociale: v }))} testid="si-rag" />
                  <Field label="Partita IVA" value={contraente.partita_iva} onChange={(v) => setContraente((c) => ({ ...c, partita_iva: v }))} testid="si-piva" />
                  <Field label="Codice fiscale" value={contraente.codice_fiscale} onChange={(v) => setContraente((c) => ({ ...c, codice_fiscale: v }))} testid="si-cf" />
                  <Field label="Comune" value={contraente.comune} onChange={(v) => setContraente((c) => ({ ...c, comune: v }))} testid="si-comune" />
                  <Field label="Indirizzo" value={contraente.indirizzo} onChange={(v) => setContraente((c) => ({ ...c, indirizzo: v }))} testid="si-indirizzo" />
                  <Field label="Email" value={contraente.email} onChange={(v) => setContraente((c) => ({ ...c, email: v }))} testid="si-email" />
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <p className="font-medium text-slate-800 mb-2">Bozza MUP — {sel.routing?.prodotto_proposto || est.ramo || "prodotto da selezionare"}</p>
                <p className="text-xs text-slate-500 mb-3">Le esigenze assicurative NON vengono dedotte dal documento: le compila l'operatore con il cliente.</p>
                {(est.dati_contratto) && (
                  <div className="text-sm text-slate-600 space-y-1">
                    <div>N. polizza origine: <b>{est.dati_contratto.numero_polizza || "—"}</b> · Premio: <b>{est.dati_contratto.premio_annuo != null ? `€ ${Number(est.dati_contratto.premio_annuo).toLocaleString("it-IT", { minimumFractionDigits: 2 })}` : "—"}</b> · Scadenza: <b>{fmtIT(est.dati_contratto.scadenza)}</b></div>
                  </div>
                )}
                {est.campi_mancanti?.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs font-medium text-amber-700 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> Informazioni da acquisire:</p>
                    <ul className="list-disc list-inside text-sm text-slate-600 mt-1">{est.campi_mancanti.map((c, i) => <li key={i}>{c}</li>)}</ul>
                  </div>
                )}
              </div>

              {sel.estratto && <SiClassificazione key={sel.id} si={sel} onSaved={() => openDetail(sel.id)} />}

              {sel.stato === "confermato" ? (
                <Button onClick={() => navigate(sel.deep_link)} className="bg-cyan-700 hover:bg-cyan-800" data-testid="si-open-quote-btn"><CheckCircle2 className="h-4 w-4 mr-1" /> Apri {tgtLabel}</Button>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {sel.match_anagrafica && <Button onClick={() => conferma(true)} disabled={confirming} variant="outline" data-testid="si-confirm-link-btn">Collega all'esistente e prosegui</Button>}
                  <Button onClick={() => conferma(false)} disabled={confirming} className="bg-cyan-700 hover:bg-cyan-800" data-testid="si-confirm-new-btn">
                    {confirming ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />} Crea anagrafica e apri {tgtLabel}
                  </Button>
                </div>
              )}
            </div>
          )}
        </main>
      </div>

      <Dialog open={showCfg} onOpenChange={setShowCfg}>
        <DialogContent className="max-w-lg" data-testid="si-settings-dialog">
          <DialogHeader><DialogTitle>Impostazioni Smart Import</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm">Modello AI per estrazione / classificazione</Label>
              <Select value={cfgForm.extraction_model} onValueChange={(v) => setCfgForm((f) => ({ ...f, extraction_model: v }))}>
                <SelectTrigger data-testid="si-model-select" className="mt-1"><SelectValue placeholder="Seleziona modello" /></SelectTrigger>
                <SelectContent>{(cfg?.modelli_disponibili || []).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-xs text-slate-400 mt-1">Default consigliato: gpt-5.4. Puoi passare a claude-sonnet-4-6 e confrontare sui tuoi documenti reali.</p>
            </div>
            <div className="border-t pt-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm">Arricchimento dati impresa da P.IVA</Label>
                <Switch checked={!!cfgForm.visura_enabled} onCheckedChange={(v) => setCfgForm((f) => ({ ...f, visura_enabled: v }))} data-testid="si-visura-enabled" />
              </div>
              <p className="text-xs text-slate-400 mt-1">Attivalo solo dopo aver inserito endpoint e chiave del provider. Finché è spento si usa solo VIES.</p>
              {cfgForm.visura_enabled && (
                <div className="grid grid-cols-1 gap-2 mt-3">
                  <button type="button" data-testid="si-visura-preset-openapi"
                    onClick={() => setCfgForm((f) => ({ ...f, visura_endpoint: "https://visurecamerali.openapi.it/impresa/{piva}", visura_method: "GET", visura_piva_param: "partita_iva", visura_auth_header: "Authorization", visura_auth_prefix: "Bearer ", visura_fonte: "Registro Imprese (Openapi)" }))}
                    className="text-xs font-medium px-3 py-1.5 rounded-lg border border-cyan-300 text-cyan-800 bg-cyan-50 hover:bg-cyan-100 transition-colors w-fit">
                    Usa preset Openapi Visure Camerali
                  </button>
                  <p className="text-xs text-slate-400">Premi il preset, poi incolla solo il token Openapi nel campo chiave e salva.</p>
                  <Input placeholder="Endpoint (usa {piva} per sostituire la P.IVA, opzionale)" value={cfgForm.visura_endpoint || ""} onChange={(e) => setCfgForm((f) => ({ ...f, visura_endpoint: e.target.value }))} data-testid="si-visura-endpoint" />
                  <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="Metodo (GET/POST)" value={cfgForm.visura_method || "GET"} onChange={(e) => setCfgForm((f) => ({ ...f, visura_method: e.target.value }))} data-testid="si-visura-method" />
                    <Input placeholder="Nome parametro P.IVA" value={cfgForm.visura_piva_param || "partita_iva"} onChange={(e) => setCfgForm((f) => ({ ...f, visura_piva_param: e.target.value }))} data-testid="si-visura-param" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="Header auth (es. Authorization)" value={cfgForm.visura_auth_header || ""} onChange={(e) => setCfgForm((f) => ({ ...f, visura_auth_header: e.target.value }))} data-testid="si-visura-header" />
                    <Input placeholder="Prefisso (es. 'Bearer ')" value={cfgForm.visura_auth_prefix ?? ""} onChange={(e) => setCfgForm((f) => ({ ...f, visura_auth_prefix: e.target.value }))} data-testid="si-visura-prefix" />
                  </div>
                  <Input type="password" placeholder={cfg?.visura_key_set ? "Chiave impostata — lascia vuoto per non cambiarla" : "Chiave API del provider"} value={cfgForm.visura_api_key || ""} onChange={(e) => setCfgForm((f) => ({ ...f, visura_api_key: e.target.value }))} data-testid="si-visura-key" />
                  <Input placeholder="Nome fonte (per tracciabilità, es. openapi.it)" value={cfgForm.visura_fonte || ""} onChange={(e) => setCfgForm((f) => ({ ...f, visura_fonte: e.target.value }))} data-testid="si-visura-fonte" />
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCfg(false)}>Annulla</Button>
            <Button onClick={saveCfg} className="bg-cyan-700 hover:bg-cyan-800" data-testid="si-settings-save">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
